import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const bioParagraphs = [
  <>
    Keaton Brown is emerging as a versatile baritone, establishing an expansive career as an opera, musical theatre, oratorio and teaching artist performer.
  </>,
  <>
    This summer Keaton Brown is a <strong>2026 Resident Artist</strong> with <strong>Opera North</strong> in New Hampshire. He will be seen as <strong>Perchik</strong> in <strong>Fiddler on the Roof</strong>, <strong>Crony/Dandy</strong> in <strong>The Ballad of Baby Doe</strong>, and he will cover <strong>Dandini</strong> in <strong>La Cenerentola</strong>. In the <strong>2025–26 season</strong>, Keaton Brown made many important debuts. On the operatic stage, he debuted with <strong>HOPERA</strong> in their highly acclaimed production of <strong>Cozy</strong>, a new adaptation of Mozart&apos;s <strong>Così Fan Tutte</strong>. On the concert stage, Keaton Brown debuted with <strong>Apollo Chamber Players</strong> to perform the world premiere of Daniel Bernard Roumain&apos;s <strong>And Still We Crossed</strong>. The piece was also recorded for album release in July 2026. He also joined <strong>Bach Society Houston</strong> as a soloist in <strong>Bach&apos;s Christmas Oratorio</strong>, and he sang with the <strong>Brazosport Symphony Orchestra</strong> as a soloist in <strong>Handel&apos;s Messiah</strong>. In the Spring, Keaton Brown worked with <strong>Houston Grand Opera To Go</strong> to perform as the Horse/Rabbit/Doctor in <strong>The Velveteen Rabbit</strong>, a touring production to schools across the city of Houston.
  </>,
  <>
    In the <strong>2024–25 season</strong>, Keaton Brown was a <strong>Mary Ragland Emerging Artist</strong> with <strong>Nashville Opera</strong>. At <strong>Nashville Opera</strong>, he was seen as Boatswain in <strong>HMS Pinafore</strong> and Papa Bear in their touring production of <strong>Goldie and The Three Singing Bears</strong>. He also performed his first <strong>Beethoven 9</strong> as a soloist with the <strong>Symphony of Southeast Texas</strong>.
  </>,
  <>
    Keaton Brown spent two summers as an emerging artist with <strong>Seagle Festival</strong>. He was seen in the title role of Mozart&apos;s <strong>Don Giovanni</strong> and Fredrick Egermann in <strong>A Little Night Music</strong>. He also spent one summer as a <strong>Manetti Shrem Artist</strong> with <strong>Festival Napa Valley</strong>, where he was seen as Il Notaio in <strong>Gianni Schicchi</strong>. Keaton Brown also was an invited guest artist with the <strong>Moores Opera Center</strong> at the University of Houston to perform his first <strong>Guglielmo</strong> in <strong>Così Fan Tutte</strong>, and he performed <strong>Papageno</strong> in <strong>Die Zauberflöte</strong> with <strong>Luke Housner&apos;s Summer Opera Workshop</strong>. Additionally, Keaton Brown sang <strong>Mozart&apos;s Requiem</strong> as a soloist in collaboration with <strong>St. Paul&apos;s United Methodist Church</strong> and <strong>Mercury Chamber Orchestra</strong>.
  </>,
  <>
    Keaton Brown continues to be in demand for teaching artists engagements. He has completed two opera touring productions, both with <strong>Houston Grand Opera</strong> and <strong>Nashville Opera</strong>. He was a young artist with <strong>DACAMERA</strong> for two seasons, where he performed concerts throughout the Houston area, taught music curriculum in classrooms, and performed with the <strong>ExxonMobil Discovery Series</strong>, bringing students to Hobby Center Houston for educational performances. He has also worked as a teaching artist with <strong>Musiqua Houston</strong>, and he maintains private voice studios at <strong>Episcopal High School</strong> and <strong>Annunciation Orthodox School</strong> in Houston, TX.
  </>,
  <>
    Keaton Brown received his <strong>Master of Music</strong> from <strong>Rice University&apos;s Shepherd School of Music</strong>, where he performed as <strong>Masetto</strong> in <strong>Don Giovanni</strong>, Harry Easter in <strong>Street Scene</strong>, and Soldato II in <strong>L&apos;Incoronazione di Poppea</strong>. He was also seen as the soloist in <strong>Durufl&eacute;&apos;s Requiem</strong> with the <strong>Rice Chorale</strong>. Keaton Brown received his <strong>Bachelor of Music</strong> from <strong>San Francisco Conservatory of Music</strong>. Notable roles included Bluff in <strong>The Impresario</strong>, Frank in <strong>Die Fledermaus</strong>, Matt in <strong>The Fantasticks</strong> and Tiger Brown in <strong>The Threepenny Opera</strong>. Keaton Brown is originally from Houston, TX, where he continues to be based.
  </>,
];

export default function Biography() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.classList.add('reveal-fade-up');
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-visible');
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      'name': 'About Keaton Brown — Baritone',
      'description': 'Keaton Brown is a versatile baritone with an expansive career in opera, musical theatre, oratorio and teaching artistry. Recent engagements include Opera North, HOPERA, Apollo Chamber Players, and Houston Grand Opera.',
      'url': 'https://www.keatonbrown.com/about',
      'mainEntity': {
        '@type': 'Person',
        'name': 'Keaton Brown',
        'jobTitle': 'Baritone',
        'description': 'Versatile baritone performing opera, concert work, and musical theatre. Based in Houston, Texas.',
        'image': 'https://storage.readdy-site.link/project_files/16bbb114-aa58-4d3d-8beb-402cd1b38a90/9f9796c5-372a-4213-857e-75ad0f4fc1c1_compressed_About-Page.webp',
        'alumniOf': [
          {
            '@type': 'CollegeOrUniversity',
            'name': "Rice University's Shepherd School of Music"
          },
          {
            '@type': 'CollegeOrUniversity',
            'name': 'San Francisco Conservatory of Music'
          }
        ],
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Houston',
          'addressRegion': 'TX',
          'addressCountry': 'US'
        }
      }
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <section className="relative bg-background-50 px-6 md:px-10 lg:px-16 py-20 md:py-28">
      <div ref={contentRef} className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Headshot sidebar */}
          <div className="lg:col-span-2">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden img-hover-zoom">
                <img
                  src="https://storage.readdy-site.link/project_files/16bbb114-aa58-4d3d-8beb-402cd1b38a90/9f9796c5-372a-4213-857e-75ad0f4fc1c1_compressed_About-Page.webp"
                  alt="Keaton Brown — Baritone"
                  className="w-full h-full object-cover object-top"
                  style={{ imageRendering: '-webkit-optimize-contrast' }}
                />
              </div>
            </div>
          </div>

          {/* Biography text */}
          <div className="lg:col-span-3 stagger-children">
            <div className="space-y-5">
              {bioParagraphs.map((paragraph, pIdx) => (
                <p
                  key={pIdx}
                  className={`text-foreground-600 leading-relaxed text-[15px] md:text-base ${pIdx > 0 ? 'mt-16 md:mt-20' : ''}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-16 md:mt-20 pt-12 border-t border-foreground-100">
              <p className="text-foreground-500 text-sm italic leading-relaxed">
                For performance and teaching inquiries, please visit the{' '}
                <Link to="/contact" className="text-accent-600 hover:text-accent-500 underline underline-offset-4 transition-colors duration-300">
                  contact page
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}